import { useMemo } from 'react';
import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';

import { ServiceCard } from './ServiceCard';
import RequestForConsultationBanner from './RequestForConsultationBanner';
import { useProductFamilies } from '../hooks/useProductFamilies';
import { useServiceStatusInfo } from '../hooks/useServiceStatusInfo';
import { useActor } from '../../../components/hooks/useActor';

interface CRSCatalogProps {
  isShownInMyServices?: boolean;
  isErrorVisible?: boolean;
}

const ServiceCatalog = ({
  isErrorVisible = true,
  isShownInMyServices = false,
}: CRSCatalogProps) => {
  const location = useLocation();
  const theme = useTheme();
  const actor = useActor();

  // Detect if current user is in Pool tenant
  const isPoolTenant = actor.isPool || location.pathname.startsWith('/pool');

  const {
    data: productFamilies = [],
    isLoading,
    isError,
    error,
  } = useProductFamilies();

  // Call useServiceStatusInfo for all tenants - the hook will handle Pool tenant logic internally
  const { data: serviceStatusInfos = [] } = useServiceStatusInfo();

  const activeProductFamilies = productFamilies.filter(
    (productFamily) => productFamily.active
  );

  const activeProductFamiliesWithoutUpcoming = activeProductFamilies.filter(
    (productFamily) => !productFamily.upcomingService
  );

  const activatedProductFamilyCount = useMemo(() => {
    // For Pool tenants, we don't track service activation status
    if (isPoolTenant) {
      return 0;
    }
    return new Set(serviceStatusInfos.map((info) => info.productFamily.id))
      .size;
  }, [serviceStatusInfos, isPoolTenant]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError && !isShownInMyServices) {
    if (!isErrorVisible) {
      return null;
    }

    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
        flexDirection="column"
        padding="2rem"
        bgcolor={theme.palette.background.paper}
        borderRadius="8px"
        boxShadow={`0 0 10px ${theme.palette.common.lightestGray}`}
      >
        <img
          src="images/Error_Cows.png"
          alt="Error illustration"
          style={{
            width: '100%',
            maxWidth: '768px',
            height: 'auto',
            marginBottom: '1rem',
          }}
        />
        <Typography
          variant="h6"
          color="error"
          width="500px"
          textAlign="center"
          gutterBottom
        >
          Failed to load resilience services. Please check your internet
          connection or try again later.
        </Typography>
        <Typography variant="body1" color="textSecondary" align="center">
          {error?.message ||
            'An unexpected error occurred. Please try again later.'}
        </Typography>
      </Box>
    );
  }

  if (
    isShownInMyServices &&
    activatedProductFamilyCount >= activeProductFamiliesWithoutUpcoming.length
  ) {
    return null;
  }

  // Show consultation banner for both Customer and Pool tenants in their catalog pages
  const shouldShowConsultationBanner =
    location.pathname === '/customer/resilience/catalog' ||
    location.pathname === '/pool/resilience/catalog';

  return (
    <Box display="grid" {...(!isShownInMyServices && { margin: '0px 4px' })}>
      {shouldShowConsultationBanner && <RequestForConsultationBanner />}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'max-content',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h5" fontWeight={700} sx={{ margin: '2rem 0' }}>
          {isShownInMyServices
            ? 'Explore our other Cowbell Resiliency Services'
            : 'Explore our Cowbell Resiliency Services'}
        </Typography>
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(1, 1fr)',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gridAutoRows: '1fr',
          gap: 4,
        }}
      >
        {Array.isArray(activeProductFamilies) &&
          activeProductFamilies.length > 0 &&
          activeProductFamilies
            .sort((a, z) => a.order - z.order)
            .filter((pf) => {
              if (isShownInMyServices) {
                return !pf.upcomingService;
              }

              return true;
            })
            .map((productFamily) => {
              return (
                <ServiceCard
                  key={productFamily.id}
                  productFamily={productFamily}
                  serviceStatusInfos={serviceStatusInfos}
                  isPoolTenant={isPoolTenant}
                />
              );
            })}
      </Box>
    </Box>
  );
};

export default ServiceCatalog;
